import type { ReactNode } from 'react';

/**
 * A value against a target — one row of the Plan-vs-Actual panel.
 *
 * Plain divs rather than a chart. Three rectangles do not justify a charting
 * library, and a bullet row reads better than a bar chart of three bars anyway:
 * the comparison is value-to-its-own-target, not category-to-category.
 *
 * THE TRACK IS `bg-tint`, NOT A LIGHTER PRIMARY. The usual advice is to tint the
 * unfilled track with a lighter step of the fill's own ramp, but the brand has no
 * `primary-100` and §1.1 forbids writing an off-brand hex as an arbitrary value.
 * `tint #F1F7FC` is a real token and is blue-tinted, so the family still reads;
 * the inset ring gives the track an edge at low fills where it would otherwise
 * vanish into the card.
 *
 * The fill is capped at 100% while the LABEL is not: over-attainment is real and
 * should be legible as "112%", but a bar that overflows its track reads as a
 * rendering bug rather than as good news.
 */
export function Meter({
  label,
  valueLabel,
  targetLabel,
  percentage,
  badge,
  hint,
}: {
  label: string;
  /** Already formatted — e.g. "842". */
  valueLabel: string;
  /** Already formatted — e.g. "900", or "—" when no target is set. */
  targetLabel: string;
  /** 0–n, or null when there is no target to measure against. */
  percentage: number | null;
  badge?: ReactNode;
  hint?: string;
}) {
  const filled = percentage === null ? 0 : Math.max(Math.min(percentage, 100), 0);

  return (
    <div>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <p className="text-sm font-semibold text-navy">{label}</p>
        <p className="text-sm tabular-nums text-muted">
          <span className="font-semibold text-ink">{valueLabel}</span>
          {' / '}
          {targetLabel}
        </p>
      </div>

      <div className="mt-2 flex items-center gap-3">
        <div
          className="relative h-2.5 min-w-0 flex-1 overflow-hidden rounded-full bg-tint ring-1 ring-inset ring-line"
          role="img"
          aria-label={`${label}: ${valueLabel} dari target ${targetLabel}`}
        >
          <div
            className="h-full rounded-full bg-primary-700 transition-[width] duration-300"
            style={{ width: `${filled}%` }}
          />
        </div>
        {badge}
      </div>

      {hint ? <p className="mt-1.5 text-sm text-muted">{hint}</p> : null}
    </div>
  );
}
